// Which exchange race is which card race, and which runner is which runner.
//
// Two sources describe the same afternoon and neither knows about the other.
// PuntersEdge prices a card of AU/NZ races and stamps every row with its own
// curated venue identity (`venue_id` / `venue_canonical` / `venue_site`, from
// its venue registry); Betfair publishes WIN markets whose venue is whatever
// the exchange calls the track that day, sponsor prefix and all. The join has
// to be exact, because the cost of getting it wrong is not a missing column —
// it is Randwick's prices printed against Rosehill's runners, which nobody
// would ever notice on the screen and every strategy would then bet on.
//
// SO NOTHING HERE GUESSES. Four things have to agree before two races are the
// same race, and a runner joins on its cloth number before its name:
//
//   1. the CODE          horse / harness / greyhound
//   2. the VENUE         slugged, sponsor-stripped, through the alias table
//   3. the RACE NUMBER   R5 is R5
//   4. the START         within ten minutes either way
//
// Anything left over is logged and dropped. A race the exchange has and the
// card does not is not a race — it is an overseas meeting, a track the card
// does not carry, or a spelling nobody has taught this module yet, and the fix
// for the third is a row in the table below rather than a looser rule here.
//
// WHY THE CODE IS PART OF IT, and this is the one that bites: Bendigo, Ballarat,
// Sandown, Albion Park and Healesville all run more than one code, and several
// run two on the same day. Venue + race number + start time alone would happily
// price a greyhound field off the gallops. Betfair cannot tell harness from
// gallops at all — the trots live inside event type 7 — so `toRaces` marks
// those `horse-or-harness` and the CARD, which knows which it is, decides.

import { slug } from '../engine/teams.js';
import { redactTokens } from '../betfair/protocol.js';

/** Ten minutes either way. Both sources publish a scheduled jump, not a real one. */
export const START_TOLERANCE_MS = 10 * 60 * 1000;

/**
 * What a cloth-number match has to score on the name before it is believed.
 *
 * Deliberately low: "Winx" against "Winx (NZ)" scores ~0.9 and a genuine pair
 * is never near this floor. It exists to catch the one case that matters — two
 * different fields joined on the same number because the races were not the
 * same race after all.
 */
const CLOTH_NAME_FLOOR = 0.2;

/** What a NAME alone has to score to stand in for a cloth number. */
const NAME_MATCH = 0.6;

/** Sponsors rename tracks by the season; the track is the part after the money. */
const SPONSOR = /^(?:sportsbet|ladbrokes|neds|tabtouch|tab|picklebet|bet365|pointsbet|palmerbet|unibet|betr|racing-com)-/;

/** "…-greyhounds", "…-racecourse": what the venue is, not which venue it is. */
const DECORATION = /-(?:greyhounds|greyhound|racecourse|race-club|racing-club|trots|harness|paceway)$/;

/**
 * Betfair's spelling → PuntersEdge's `venue_id`.
 *
 * ONLY THE ROWS THAT GENUINELY DIFFER. Flemington, Caulfield, Eagle Farm,
 * Doomben, Morphettville, Gosford, Warwick Farm, Bendigo, Ipswich, Gawler,
 * Pinjarra and the rest need no row at all: both sides slug to the same string,
 * and a table full of identities is a table nobody can read.
 *
 * The canonical side is checked, not assumed: `venue_id` / `venue_canonical`
 * pairs published in PuntersEdge's own OpenAPI give `randwick`/"Randwick" (not
 * Royal Randwick), `rosehill`/"Rosehill" (not Rosehill Gardens),
 * `sandown-hillside`/"Sandown Hillside", `sandown-park`/"Sandown Park",
 * `caulfield`, `doomben`, `eagle-farm`, `healesville`, `grafton`, `moe`,
 * `tatura`, `broome`, `kalgoorlie`.
 *
 * A BARE `sandown` IS NOT IN HERE ON PURPOSE. Hillside, Lakeside and the
 * greyhound park all answer to it, and one of the three is a coin toss. It logs
 * as unmatched, which is the honest answer.
 */
export const VENUE_ALIASES = Object.freeze({
  // thoroughbreds
  'royal-randwick': 'randwick',
  'randwick-kensington': 'randwick',
  'rosehill-gardens': 'rosehill',
  kembla: 'kembla-grange',
  broadmeadow: 'newcastle',
  'newcastle-broadmeadow': 'newcastle',
  'canterbury-park': 'canterbury',
  'the-valley': 'moonee-valley',
  'moonee-valley-racecourse': 'moonee-valley',
  'ladbrokes-park-hillside': 'sandown-hillside',
  'park-hillside': 'sandown-hillside',
  'sandown-hside': 'sandown-hillside',
  'ladbrokes-park-lakeside': 'sandown-lakeside',
  'park-lakeside': 'sandown-lakeside',
  'sandown-lside': 'sandown-lakeside',
  'corbould-park': 'sunshine-coast',
  'clifford-park': 'toowoomba',
  'ascot-perth': 'ascot',
  'perth-ascot': 'ascot',
  'belmont-park': 'belmont',
  elwick: 'hobart',
  mowbray: 'launceston',
  'fannie-bay': 'darwin',
  'thoroughbred-park': 'canberra',
  'riccarton-park': 'riccarton',
  // greyhounds
  'sandown-greyhounds': 'sandown-park',
  'sandown-park-greyhounds': 'sandown-park',
  meadows: 'the-meadows',
  'the-meadows-greyhounds': 'the-meadows',
  'wentworth-park-greyhounds': 'wentworth-park',
  'the-gardens-newcastle': 'the-gardens',
  'albion-park-greyhounds': 'albion-park',
  'angle-park-greyhounds': 'angle-park',
  'cannington-greyhounds': 'cannington',
  'healesville-greyhounds': 'healesville',
});

/**
 * A venue name → the id both sides agree on.
 *
 * The raw slug is looked up first, so an alias can name a spelling that would
 * otherwise be stripped; then the sponsor prefix and the decoration come off
 * and it is looked up again. A name that is nothing but sponsor keeps its raw
 * slug rather than becoming the empty string.
 */
export function venueId(name) {
  const raw = slug(name);
  if (!raw) return '';
  if (VENUE_ALIASES[raw]) return VENUE_ALIASES[raw];
  let stripped = raw;
  for (let before = ''; stripped !== before;) {
    before = stripped;
    stripped = stripped.replace(SPONSOR, '').replace(DECORATION, '');
  }
  if (!stripped) return raw;
  return VENUE_ALIASES[stripped] ?? stripped;
}

/** `racing_horse:2026-09-15:randwick:R5` → its four parts, or empty strings. */
const keyParts = (card) => String(card?.matchKey ?? '').split(':');

const CARD_CATEGORY = Object.freeze({
  racing_horse: 'horse',
  racing_harness: 'harness',
  racing_greyhound: 'greyhound',
});

/** horse | harness | greyhound, from the card's sport — or '' when it says nothing. */
export function cardCategory(card) {
  const sport = card?.sport ?? keyParts(card)[0];
  return CARD_CATEGORY[sport] ?? '';
}

/**
 * The card's venue id, whichever of the four names the board gives it — and the
 * matchKey as the last resort, since the slug is baked into it.
 */
export function cardVenueId(card) {
  return venueId(card?.venueId ?? card?.venueSlug ?? card?.venue ?? card?.venueCanonical ?? keyParts(card)[2] ?? '');
}

/** The card's race number, from the field or from the `R5` in its key. */
export function cardRaceNumber(card) {
  const direct = Number(card?.raceNumber);
  if (Number.isInteger(direct) && direct > 0) return direct;
  const fromKey = Number(/^R(\d{1,2})$/i.exec(keyParts(card)[3] ?? '')?.[1]);
  return Number.isInteger(fromKey) && fromKey > 0 ? fromKey : null;
}

/** The card's scheduled jump. */
const cardStart = (card) => card?.kickoff ?? card?.startTime ?? null;

/**
 * Do these two codes describe the same race?
 *
 * `horse-or-harness` is everything Betfair can say about an event type 7
 * market, so it is compatible with both — and with nothing else. An unknown
 * card code matches nothing: this module would rather drop a price than invent
 * one.
 */
export function sameCategory(cardCode, raceCategory) {
  if (!cardCode) return false;
  if (raceCategory === 'greyhound') return cardCode === 'greyhound';
  if (raceCategory === 'horse-or-harness') return cardCode === 'horse' || cardCode === 'harness';
  return false;
}

/**
 * "4. Winx (NZ)" → "winx": the cloth prefix and the country tag are not the
 * name, and neither is the punctuation. The separators go too — one source
 * writes "O'Reilly's Girl" and the other "Oreillys Girl", and those are the
 * same horse.
 */
const cleanName = (name) => slug(String(name ?? '')
  .replace(/^\s*\d{1,2}\s*[.)]\s*/, '')
  .replace(/\([a-z]{2,4}\)/gi, ' ')).replace(/-/g, '');

/**
 * How alike two runner names are, 0 to 1: Dice's coefficient over letter pairs.
 *
 * Bigrams rather than equality because the difference between two sources is
 * punctuation, a country tag and the odd apostrophe — never word order — and
 * rather than a Levenshtein distance because this only has to RANK candidates
 * that a cloth number has already narrowed to a handful.
 */
export function nameScore(a, b) {
  const x = cleanName(a);
  const y = cleanName(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  const pairs = (text) => {
    const out = [];
    for (let i = 0; i < text.length - 1; i += 1) out.push(text.slice(i, i + 2));
    return out;
  };
  const left = pairs(x);
  const right = pairs(y);
  if (!left.length || !right.length) return 0;
  const pool = new Map();
  for (const gram of left) pool.set(gram, (pool.get(gram) ?? 0) + 1);
  let hits = 0;
  for (const gram of right) {
    const spare = pool.get(gram) ?? 0;
    if (spare > 0) {
      pool.set(gram, spare - 1);
      hits += 1;
    }
  }
  return (2 * hits) / (left.length + right.length);
}

/**
 * The card's active runners as `{ selection, name, cloth }`.
 *
 * `runners[rN].number` is the cloth number; where it is missing the selection
 * key carries it, because `r4` IS runner 4 — that is what the key means.
 */
function cardRunners(card) {
  const keys = card?.selections ?? Object.keys(card?.runners ?? {});
  const out = [];
  for (const selection of keys) {
    const runner = card?.runners?.[selection] ?? {};
    const number = Number(runner.number ?? /^r(\d{1,2})$/i.exec(String(selection))?.[1]);
    out.push({
      selection,
      name: runner.name ?? '',
      cloth: Number.isInteger(number) && number > 0 ? number : null,
    });
  }
  return out;
}

/**
 * The exchange's runners joined onto the card's selections.
 *
 * CLOTH FIRST, NAME AS THE TIE-BREAK. One card runner with that number and a
 * name that is not obviously somebody else's takes it. Where the number is
 * missing, or where two runners answer to it, the name decides — and it has to
 * score well to do so. A runner that satisfies neither is returned unmatched,
 * never forced onto whatever is left.
 */
function joinRunners(card, race) {
  const pool = cardRunners(card);
  const claimed = new Set();
  const runners = [];
  const unmatchedRunners = [];

  for (const exchange of race?.runners ?? []) {
    const free = pool.filter((runner) => !claimed.has(runner.selection));
    const byCloth = exchange?.cloth == null ? [] : free.filter((runner) => runner.cloth === exchange.cloth);

    let chosen = null;
    let score = 0;
    if (byCloth.length === 1) {
      score = nameScore(byCloth[0].name, exchange.name);
      if (score >= CLOTH_NAME_FLOOR) chosen = byCloth[0];
    }
    if (!chosen) {
      const field = byCloth.length > 1 ? byCloth : free;
      let best = null;
      let bestScore = 0;
      for (const runner of field) {
        const next = nameScore(runner.name, exchange.name);
        if (next > bestScore) {
          best = runner;
          bestScore = next;
        }
      }
      if (best && bestScore >= NAME_MATCH) {
        chosen = best;
        score = bestScore;
      }
    }

    if (!chosen) {
      unmatchedRunners.push({ cloth: exchange?.cloth ?? null, name: exchange?.name ?? '' });
      continue;
    }
    claimed.add(chosen.selection);
    runners.push({
      selection: chosen.selection,
      cloth: exchange?.cloth ?? chosen.cloth,
      name: chosen.name,
      exchange,
      score,
    });
  }

  return { runners, unmatchedRunners };
}

/**
 * Is this exchange race this card race? `null` when it is not.
 *
 * When it is, the answer is the JOIN rather than a boolean — the caller wants
 * the runner pairing, and working it out twice is how the two halves drift:
 *
 *   { marketId, race, startSkewMs,
 *     runners: [{ selection, cloth, name, exchange, score }],
 *     unmatchedRunners: [{ cloth, name }] }
 */
export function matchRace(card, race, { toleranceMs = START_TOLERANCE_MS } = {}) {
  if (!card || !race) return null;
  if (!sameCategory(cardCategory(card), race.category)) return null;

  const venue = cardVenueId(card);
  if (!venue || venue !== venueId(race.venue ?? race.venueSlug)) return null;

  const number = cardRaceNumber(card);
  if (!number || number !== Number(race.raceNumber)) return null;

  const start = Date.parse(cardStart(card) ?? '');
  const jump = Date.parse(race.startTime ?? '');
  if (!Number.isFinite(start) || !Number.isFinite(jump)) return null;
  const startSkewMs = jump - start;
  if (Math.abs(startSkewMs) > toleranceMs) return null;

  return {
    marketId: race.marketId ?? null,
    race,
    startSkewMs,
    ...joinRunners(card, race),
  };
}

/**
 * Every exchange race paired with its card race, and what could not be paired.
 *
 * One card per race and one race per card: the first card a race matches takes
 * it, and neither is offered twice. The unmatched are counted in ONE log line
 * rather than one each — a card is a couple of dozen races and a punter with
 * the exchange on has every overseas meeting in the answer too.
 */
export function matchRaces(cards = [], races = [], { log = console, toleranceMs } = {}) {
  const pairs = [];
  const unmatched = [];
  const taken = new Set();

  for (const race of races) {
    let found = null;
    for (const card of cards) {
      if (taken.has(card)) continue;
      const match = matchRace(card, race, toleranceMs === undefined ? {} : { toleranceMs });
      if (match) {
        found = { card, match };
        break;
      }
    }
    if (!found) {
      unmatched.push(race);
      continue;
    }
    taken.add(found.card);
    pairs.push(found);
  }

  if (unmatched.length) {
    const named = unmatched.slice(0, 3).map((race) => `${venueId(race?.venue ?? race?.venueSlug) || '?'} R${race?.raceNumber ?? '?'}`);
    if (unmatched.length > named.length) named.push('…');
    // Venue slugs and race numbers carry nothing secret; the scrub is the house
    // rule for anything this app writes to a log, and it costs one call.
    log.warn?.(redactTokens(
      `racing: ${unmatched.length} exchange ${unmatched.length === 1 ? 'race' : 'races'} not matched to the card (${named.join(', ')})`,
    ));
  }

  return { pairs, unmatched };
}
